import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Coordinates {
  lat: number;
  lng: number;
}

// Haversine formula to calculate distance between two points in miles
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

// Simple in-memory cache for geocoding results
const geocodeCache = new Map<string, { coords: Coordinates | null; timestamp: number }>();
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Geocoding using Google Geocoding API via Edge Function
export async function geocodeLocation(query: string): Promise<Coordinates | null> {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return null;

  // Check cache first
  const cached = geocodeCache.get(normalizedQuery);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.coords;
  }

  try {
    const { data, error } = await supabase.functions.invoke('places-autocomplete', {
      body: { query: normalizedQuery, type: 'geocode' },
    });

    if (error) throw error;

    let coords: Coordinates | null = null;
    if (data?.lat != null && data?.lng != null) {
      coords = { lat: data.lat, lng: data.lng };
    }

    geocodeCache.set(normalizedQuery, { coords, timestamp: Date.now() });
    return coords;
  } catch (error) {
    console.error('Geocoding error:', error);
    throw error;
  }
}

export function useLocationSearch() {
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const pendingRequestRef = useRef<string | null>(null);

  const searchLocation = useCallback(async (query: string) => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
      setCoordinates(null);
      setLocationError(null);
      return null;
    }

    if (pendingRequestRef.current === normalizedQuery) return null;

    pendingRequestRef.current = normalizedQuery;
    setIsGeocoding(true);
    setLocationError(null);

    try {
      const coords = await geocodeLocation(normalizedQuery);

      if (pendingRequestRef.current === normalizedQuery) {
        if (coords) {
          setCoordinates(coords);
        } else {
          setLocationError('Location not found');
          setCoordinates(null);
        }
      }

      return coords;
    } catch (error) {
      if (pendingRequestRef.current === normalizedQuery) {
        setLocationError(error instanceof Error ? error.message : 'Location search failed');
        setCoordinates(null);
      }
      return null;
    } finally {
      if (pendingRequestRef.current === normalizedQuery) {
        setIsGeocoding(false);
        pendingRequestRef.current = null;
      }
    }
  }, []);

  const clearLocation = useCallback(() => {
    setCoordinates(null);
    setLocationError(null);
    pendingRequestRef.current = null;
  }, []);

  return {
    coordinates,
    isGeocoding,
    locationError,
    searchLocation,
    clearLocation,
  };
}
